/**
 * Benefits Section Component
 *
 * Explains why agents need defeasible logic for coordination
 * Based on: https://anuna.io/blog/hence
 */

export default function BenefitsSection() {
  return (
    <section className="mt-10 md:mt-16 w-full max-w-full overflow-x-hidden">
      <h2 className="text-xl md:text-2xl font-bold text-center mb-2">Why Agents Need This</h2>
      <p className="text-center text-sm md:text-base text-base-content/60 mb-6 md:mb-8 max-w-2xl mx-auto px-2">
        Task managers track state. Hence reasons about it.
      </p>

      {/* Comparison Table */}
      <div className="overflow-x-auto mb-8 md:mb-12 -mx-4 px-4">
        <table className="table table-xs md:table-md w-full min-w-[600px]">
          <thead>
            <tr>
              <th className="bg-base-200 w-1/4"></th>
              <th className="bg-base-200 text-center w-1/4">
                <div className="flex flex-col items-center gap-1">
                  <span className="font-bold">Hence</span>
                  <span className="text-xs font-normal text-base-content/50">Defeasible Logic</span>
                </div>
              </th>
              <th className="bg-base-300/50 text-center w-1/4">
                <div className="flex flex-col items-center gap-1">
                  <span>TODO Lists</span>
                  <span className="text-xs font-normal text-base-content/50">Basic</span>
                </div>
              </th>
              <th className="bg-base-300/70 text-center w-1/4">
                <div className="flex flex-col items-center gap-1">
                  <span>Task Managers</span>
                  <span className="text-xs font-normal text-base-content/50">Session-based</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="font-medium">Multi-agent coordination</td>
              <td className="text-center bg-base-100">
                <span className="badge badge badge-sm bg-base-300 text-base-content border-0">Append-only log</span>
              </td>
              <td className="text-center bg-base-300/30">
                <span className="text-base-content/40">-</span>
              </td>
              <td className="text-center bg-base-300/40">
                <span className="text-base-content/40">-</span>
              </td>
            </tr>
            <tr>
              <td className="font-medium">Dependency semantics</td>
              <td className="text-center bg-base-100">
                <span className="badge badge badge-sm bg-base-300 text-base-content border-0">Formal rules</span>
              </td>
              <td className="text-center bg-base-300/30">
                <span className="text-base-content/40">-</span>
              </td>
              <td className="text-center bg-base-300/40">
                <span className="badge badge-ghost badge-sm">Implicit</span>
              </td>
            </tr>
            <tr>
              <td className="font-medium">Blockers / Defeaters</td>
              <td className="text-center bg-base-100">
                <code className="badge badge badge-sm bg-base-300 text-base-content border-0 font-mono">except</code>
              </td>
              <td className="text-center bg-base-300/30">
                <span className="text-base-content/40">-</span>
              </td>
              <td className="text-center bg-base-300/40">
                <span className="text-base-content/40">-</span>
              </td>
            </tr>
            <tr>
              <td className="font-medium">Priority resolution</td>
              <td className="text-center bg-base-100">
                <code className="badge badge badge-sm bg-base-300 text-base-content border-0 font-mono">prefer</code>
              </td>
              <td className="text-center bg-base-300/30">
                <span className="text-base-content/40">-</span>
              </td>
              <td className="text-center bg-base-300/40">
                <span className="badge badge-ghost badge-sm">Manual</span>
              </td>
            </tr>
            <tr>
              <td className="font-medium">Explain why blocked</td>
              <td className="text-center bg-base-100">
                <code className="badge badge badge-sm bg-base-300 text-base-content border-0 font-mono">why-not</code>
              </td>
              <td className="text-center bg-base-300/30">
                <span className="text-base-content/40">-</span>
              </td>
              <td className="text-center bg-base-300/40">
                <span className="text-base-content/40">-</span>
              </td>
            </tr>
            <tr>
              <td className="font-medium">Hypothetical reasoning</td>
              <td className="text-center bg-base-100">
                <code className="badge badge badge-sm bg-base-300 text-base-content border-0 font-mono">what-if</code>
              </td>
              <td className="text-center bg-base-300/30">
                <span className="text-base-content/40">-</span>
              </td>
              <td className="text-center bg-base-300/40">
                <span className="text-base-content/40">-</span>
              </td>
            </tr>
            <tr>
              <td className="font-medium">Belief revision</td>
              <td className="text-center bg-base-100">
                <code className="badge badge badge-sm bg-base-300 text-base-content border-0 font-mono">assert</code>
              </td>
              <td className="text-center bg-base-300/30">
                <span className="text-base-content/40">-</span>
              </td>
              <td className="text-center bg-base-300/40">
                <span className="text-base-content/40">-</span>
              </td>
            </tr>
            <tr>
              <td className="font-medium">Backward reasoning</td>
              <td className="text-center bg-base-100">
                <code className="badge badge badge-sm bg-base-300 text-base-content border-0 font-mono">require</code>
              </td>
              <td className="text-center bg-base-300/30">
                <span className="text-base-content/40">-</span>
              </td>
              <td className="text-center bg-base-300/40">
                <span className="text-base-content/40">-</span>
              </td>
            </tr>
            <tr>
              <td className="font-medium">Trust weighting</td>
              <td className="text-center bg-base-100">
                <code className="badge badge badge-sm bg-base-300 text-base-content border-0 font-mono">trusts</code>
              </td>
              <td className="text-center bg-base-300/30">
                <span className="text-base-content/40">-</span>
              </td>
              <td className="text-center bg-base-300/40">
                <span className="text-base-content/40">-</span>
              </td>
            </tr>
            <tr>
              <td className="font-medium">Temporal reasoning</td>
              <td className="text-center bg-base-100">
                <code className="badge badge badge-sm bg-base-300 text-base-content border-0 font-mono">during</code>
              </td>
              <td className="text-center bg-base-300/30">
                <span className="text-base-content/40">-</span>
              </td>
              <td className="text-center bg-base-300/40">
                <span className="text-base-content/40">-</span>
              </td>
            </tr>
            <tr>
              <td className="font-medium">Semantic annotations</td>
              <td className="text-center bg-base-100">
                <code className="badge badge badge-sm bg-base-300 text-base-content border-0 font-mono">meta</code>
              </td>
              <td className="text-center bg-base-300/30">
                <span className="text-base-content/40">-</span>
              </td>
              <td className="text-center bg-base-300/40">
                <span className="text-base-content/40">-</span>
              </td>
            </tr>
            <tr>
              <td className="font-medium">Process mining</td>
              <td className="text-center bg-base-100">
                <code className="badge badge badge-sm bg-base-300 text-base-content border-0 font-mono">learn</code>
              </td>
              <td className="text-center bg-base-300/30">
                <span className="text-base-content/40">-</span>
              </td>
              <td className="text-center bg-base-300/40">
                <span className="text-base-content/40">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Problem/Solution comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        <div className="card bg-error/5 border border-error/20">
          <div className="card-body p-5">
            <h3 className="font-bold text-error text-lg">Self-Tracking</h3>
            <p className="text-xs text-base-content/50 mb-3">TODO lists, task managers</p>
            <ul className="space-y-2 text-sm text-base-content/70">
              <li className="flex items-start gap-2">
                <span className="text-error mt-0.5">x</span>
                <span>Single agent manages its own checklist</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-error mt-0.5">x</span>
                <span>Reader interprets dependencies on each read</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-error mt-0.5">x</span>
                <span>Semantic drift between sessions or agents</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-error mt-0.5">x</span>
                <span>No way to ask "why is this blocked?"</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="card bg-success/5 border border-success/20">
          <div className="card-body p-5">
            <h3 className="font-bold text-success text-lg">Coordination Infrastructure</h3>
            <p className="text-xs text-base-content/50 mb-3">Defeasible logic plans</p>
            <ul className="space-y-2 text-sm text-base-content/70">
              <li className="flex items-start gap-2">
                <span className="text-success mt-0.5">+</span>
                <span>Multiple agents reason over shared plan</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-success mt-0.5">+</span>
                <span>Derivation logic encoded in the file</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-success mt-0.5">+</span>
                <span>Consistent conclusions across all readers</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-success mt-0.5">+</span>
                <span>Built-in explanations and derivation chains</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <h3 className="text-xl font-semibold text-center mb-6">Design Choices</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <h4 className="font-semibold text-sm">Single File</h4>
            <p className="text-xs text-base-content/60">
              Plain text, git-friendly. Writes under 4KB are atomic on POSIX.
            </p>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <h4 className="font-semibold text-sm">Explanations</h4>
            <p className="text-xs text-base-content/60">
              See derivation chains, blockers, and what facts would enable goals.
            </p>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <h4 className="font-semibold text-sm">Hypotheticals</h4>
            <p className="text-xs text-base-content/60">
              Test "what if" scenarios without modifying the actual plan.
            </p>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <h4 className="font-semibold text-sm">Belief Revision</h4>
            <p className="text-xs text-base-content/60">
              Assert discoveries with full DSL: facts, rules, defeaters, priorities.
            </p>
          </div>
        </div>
      </div>

      {/* Closing statement */}
      <div className="mt-8 text-center">
        <blockquote className="text-base-content/70 italic max-w-xl mx-auto">
          "It seems likely that introducing nonmonotonic reasoning will not be the only modification of logic that will be required in order to give machines human capability for commonsense reasoning."
        </blockquote>
        <p className="text-sm text-base-content/50 mt-2">— John McCarthy, Mathematical Logic in Artificial Intelligence (1989)</p>
      </div>
    </section>
  );
}
